export type SvelteActionReturnType<P> = {
    update?: (newParams?: P) => void;
    destroy?: () => void;
} | void;

export type SvelteHTMLActionType<P> = (
    node: HTMLElement,
    params?: P,
) => SvelteActionReturnType<P>;

export type HTMLActionEntry<P> =
    | SvelteHTMLActionType<P>
    | [SvelteHTMLActionType<P>, P];

export type HTMLActionArray = HTMLActionEntry<any>[];

export type SvelteSVGActionType<P> = (
    node: SVGElement,
    params?: P,
) => SvelteActionReturnType<P>;

export type SVGActionEntry<P> =
    | SvelteSVGActionType<P>
    | [SvelteSVGActionType<P>, P];

export type SVGActionArray = SVGActionEntry<any>[];

export type ActionArray = HTMLActionArray | SVGActionArray;

/**
 * This neat trick allows you to "forward" actions passed into a component to one of its
 * internal DOM elements (or yet another component down the line).
 *
 * Example usage:
 *
 * ```svelte
 * <!-- Button.svelte -->
 * <script>
 *     import { useActions, type ActionArray } from '@amp/web-app-components/src/utils/use-actions';
 *     export let use: ActionArray;
 * </script>
 * <button use:useActions={use}>Drag me stuff</button>
 * ```
 *
 * ```svelte
 * <!-- Main.svelte -->
 * <script>
 *     import { autofocus } from 'actions/autofocus';
 *     import { allowDrag } from 'actions/drag-and-drop';
 * </script>
 * <Button use={[autofocus, [allowDrag, { dragOption: 'foo' }]]}
 * ```
 */
export function useActions(
    node: HTMLElement | SVGElement,
    actions: ActionArray,
) {
    const actionReturns: SvelteActionReturnType<any>[] = [];

    if (actions) {
        for (let i = 0; i < actions.length; i++) {
            const actionEntry = actions[i];
            const action = Array.isArray(actionEntry)
                ? actionEntry[0]
                : actionEntry;
            if (Array.isArray(actionEntry) && actionEntry.length > 1) {
                actionReturns.push(
                    action(node as HTMLElement & SVGElement, actionEntry[1]),
                );
            } else {
                actionReturns.push(action(node as HTMLElement & SVGElement));
            }
        }
    }

    return {
        update(newActions: ActionArray) {
            if (
                ((newActions && newActions.length) || 0) !==
                actionReturns.length
            ) {
                throw new Error(
                    'You must not change the length of an actions array.',
                );
            }

            if (newActions) {
                for (let i = 0; i < newActions.length; i++) {
                    const returnEntry = actionReturns[i];
                    if (returnEntry && returnEntry.update) {
                        const actionEntry = newActions[i];
                        if (
                            Array.isArray(actionEntry) &&
                            actionEntry.length > 1
                        ) {
                            returnEntry.update(actionEntry[1]);
                        } else {
                            returnEntry.update();
                        }
                    }
                }
            }
        },

        destroy() {
            for (let i = 0; i < actionReturns.length; i++) {
                const returnEntry = actionReturns[i];
                if (returnEntry && returnEntry.destroy) {
                    returnEntry.destroy();
                }
            }
        },
    };
}
